/**
 * Google Analytics 4 (gtag.js) integration.
 *
 * Loads the gtag script lazily and exposes helpers to identify the current
 * user (Stytch member) and the organization they belong to, plus page view
 * tracking for the SPA.
 */

type GtagCommand = 'js' | 'config' | 'event' | 'set' | 'consent'

declare global {
    interface Window {
        dataLayer: unknown[]
        gtag: (command: GtagCommand, ...args: unknown[]) => void
    }
}

const MEASUREMENT_ID = import.meta.env.GA_MEASUREMENT_ID as string | undefined

let initialized = false

const isEnabled = (): boolean => Boolean(MEASUREMENT_ID) && typeof window !== 'undefined'

/**
 * Inject the gtag.js script and prime the dataLayer. Safe to call multiple
 * times — subsequent calls are no-ops.
 *
 * `send_page_view` is disabled because we drive page views manually from the
 * router (see `trackPageView`).
 */
export const initAnalytics = (): void => {
    if (initialized || !isEnabled()) {
        return
    }

    initialized = true

    window.dataLayer = window.dataLayer || []
    // Important: must push the `arguments` object (not a rest array) so that
    // gtag.js can pick the queued commands up correctly.
    function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments)
    }
    window.gtag = gtag as unknown as Window['gtag']

    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`
    document.head.appendChild(script)

    window.gtag('js', new Date())
    window.gtag('config', MEASUREMENT_ID as string, {
        send_page_view: false,
        anonymize_ip: true,
    })
}

/**
 * Associate the current GA session with the authenticated user. Sets both
 * `user_id` and custom user properties so the IDs can be used in reports and
 * audiences.
 */
export const identifyUser = (params: { memberId: string; organizationId: string }): void => {
    if (!isEnabled() || !window.gtag) {
        return
    }

    window.gtag('config', MEASUREMENT_ID as string, {
        user_id: params.memberId,
        send_page_view: false,
    })

    window.gtag('set', 'user_properties', {
        member_id: params.memberId,
        organization_id: params.organizationId,
    })
}

/**
 * Clear identifying information when the user signs out.
 */
export const resetUser = (): void => {
    if (!isEnabled() || !window.gtag) {
        return
    }

    window.gtag('config', MEASUREMENT_ID as string, {
        user_id: null,
        send_page_view: false,
    })

    window.gtag('set', 'user_properties', {
        member_id: null,
        organization_id: null,
    })
}

/**
 * Manually emit a page_view event. Called whenever the SPA route changes.
 */
export const trackPageView = (path: string, title?: string): void => {
    if (!isEnabled() || !window.gtag) {
        return
    }

    window.gtag('event', 'page_view', {
        page_path: path,
        page_location: window.location.origin + path,
        page_title: title ?? document.title,
    })
}
